import { Component, OnInit, Input, OnDestroy } from '@angular/core';
import { Allocation } from '../../../common/models/allocation';
import { CommonModule } from '@angular/common';
import { MatStepperModule } from '@angular/material/stepper';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { ActivatedRoute, RouterModule } from '@angular/router';
import { MatDividerModule } from '@angular/material/divider';
import { AllocationService } from '../../../common/services/allocation.service';
import { Guid } from 'guid-typescript';
import { FileService } from '../../../common/services/file.service';
import { FileInfo } from '../../../common/models/file';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { BudgetItem } from '../../../common/models/submission';
import { CostType } from '../../../common/models/common';
import { Subject, takeUntil } from 'rxjs';

@Component({
  selector: 'app-allocation-view',
  templateUrl: './allocation.component.html',
  styleUrls: ['./allocation.component.scss'],
  imports: [
    CommonModule,
    MatStepperModule,
    MatCardModule,
    MatIconModule,
    MatButtonModule,
    MatDividerModule,
    RouterModule
],
  standalone: true
})
export class AllocationComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();

  isHandset: boolean = false;
 
   @Input() allocation!: Allocation;
 
   allocationFiles: FileInfo[] = [];
   budgetItemFileMap: { [budgetItemId: string]: FileInfo[] } = {};
 
   // Field definitions to match allocation component fields exactly
   generalInfoFields = [
     { label: 'Nositelj aktivnosti (naziv udruge/organizacije civilnog društva)', property: 'organizationName', type: 'text' },
     { label: 'Registrarski broj u odgovarajućem registru (Registar udruga, Registar zaklada i dr.-Ministarstvo uprave)', property: 'registrationNumber', type: 'text' },
     { label: 'OIB', property: 'oib', type: 'text' },
     { label: 'Broj u Registru neprofitnih organizacija (RNO broj - Ministarstvo financija)', property: 'rno', type: 'text' },
     { label: 'Adresa udruge/organizacije civilnog društva (ulica i broj)', property: 'address', type: 'text' },
     { label: 'Poštanski broj i sjedište', property: 'postalCodeAndHeadquarters', type: 'text' },
     { label: 'Županija', property: 'county', type: 'text' },
     { label: 'Broj telefona i telefaksa udruge/organizacije civilnog društva', property: 'telephone', type: 'text' },
     { label: 'Adresa e-pošte udruge/organizacije civilnog društva', property: 'organizationEmail', type: 'text' },
     { label: 'Internetska stranica udruge/organizacije civilnog društva', property: 'website', type: 'text' },
     { label: 'Ime i prezime osobe ovlaštene za zastupanje udruge/organizacije civilnog društva', property: 'authorizedRepresentative', type: 'text' },
     { label: 'Naziv banke', property: 'bankName', type: 'text' },
     { label: 'IBAN', property: 'bankAccountNumber', type: 'text' }
   ];
 
   activityInfoFields = [
     { label: 'Naziv aktivnosti', property: 'activityName', type: 'text' },
     { label: 'Ime i prezime voditelja aktivnosti', property: 'projectLeader', type: 'text' },
     { label: 'Zatraženi iznos', property: 'amountRequestedFromMunicipality', type: 'currency' },
     { label: 'Datum i vrijeme početka provedbe aktivnosti', property: 'implementationStart', type: 'date' },
     { label: 'Datum i vrijeme završetka provedbe aktivnosti', property: 'implementationEnd', type: 'date' },
     { label: 'Kratki opis aktivnosti', property: 'activityDescription', type: 'text' },
     { label: 'Područje djelovanja udruge/organizacije civilnog društva kojima se ostvaruju ciljevi propisani statutom organizacije', property: 'fieldOfOperation', type: 'text' },
     { label: 'Ciljane skupine (udruge/organizacije civilnog društva mogu imati jednu ili više ciljanih skupina s obzirom na članstvo i/ili korisnike prema kojima je organizacija usmjerena)', property: 'targetGroups', type: 'text' },
     { label: 'Teritorijalna zastupljenost', property: 'territorialCoverage', type: 'text' },
     { label: 'Uključivanje volontera - sudjeluju li volonteri? (DA/NE)', property: 'volunteersInvolved', type: 'bool' },
     { label: 'Broj potencijalno uključenih volontera', property: 'potentialVolunteerCount', type: 'number' }
   ];
 
   justificationFields = [
     { label: 'Obrazloženje (navedite zašto tražite izravnu dodjelu sredstava)', property: 'justification', type: 'text' }
   ];
 
   // Processed field arrays (including type) used in the template
   generalInfoFieldValues: Array<{ label: string; property: string; value: any; type: string }> = [];
   activityInfoFieldValues: Array<{ label: string; property: string; value: any; type: string }> = [];
   justificationFieldValues: Array<{ label: string; property: string; value: any; type: string }> = [];
 
   constructor(
     private allocationService: AllocationService,
     private route: ActivatedRoute, 
     private fileService: FileService,
     private breakpointObserver: BreakpointObserver
   ) {}
 
   ngOnInit(): void {
     const allocationIdParamName = 'allocationId';
     this.route.paramMap.pipe(takeUntil(this.destroy$)).subscribe((params) => {
       if (params.get(allocationIdParamName)) {
         const allocationIdParam = params.get(allocationIdParamName);
         if (!allocationIdParam) {
           return;
         }
         const allocationId = Guid.parse(allocationIdParam);
         this.allocationService.getAllocation(allocationId).pipe(takeUntil(this.destroy$)).subscribe((allocation) => {
           this.allocation = allocation;    
           this.processFields();  
           this.loadBudgetItemAttachments();  
           this.loadAllocationAttachments(); 
         });
       }
     });
 
     this.breakpointObserver.observe([Breakpoints.XSmall, Breakpoints.Small]).pipe(takeUntil(this.destroy$))
     .subscribe(result => {
       this.isHandset = result.matches;
     });
   }

   ngOnDestroy() {
      this.destroy$.next();
      this.destroy$.complete();
   }
 
   processFields(): void {
     this.generalInfoFieldValues = this.generalInfoFields.map((field: { label: string; property: string; type: string }) => ({
       label: field.label,
       property: field.property,
       value: (this.allocation as any)[field.property],
       type: field.type
     }));
 
     this.activityInfoFieldValues = this.activityInfoFields.map((field: { label: string; property: string; type: string }) => ({
       label: field.label,
       property: field.property,
       value: (this.allocation as any)[field.property],
       type: field.type
     }));
 
     this.justificationFieldValues = this.justificationFields.map((field: { label: string; property: string; type: string }) => ({
       label: field.label,
       property: field.property,
       value: (this.allocation as any)[field.property],
       type: field.type
     }));
   }
 
   get directBudgetItems(): BudgetItem[] {
     return this.allocation?.budgetPlan?.budgetItems?.filter(
       item => item.type === CostType.Direct
     ) || [];
   }
 
   get indirectBudgetItems(): BudgetItem[] {
     return this.allocation?.budgetPlan?.budgetItems?.filter(
       item => item.type === CostType.Indirect
     ) || [];
   }
 
   private loadAllocationAttachments(): void {
     if (!this.allocation?.fileStorageLocation) {
       return;
     }
     this.fileService.listDocuments(this.allocation.fileStorageLocation).pipe(takeUntil(this.destroy$)).subscribe({
       next: (files) => {
         this.allocationFiles = files;
       },
       error: (err) => {
         console.error('Error loading allocation attachments', err);
       }
     });
   }
 
   private loadBudgetItemAttachments(): void {
     if (!this.allocation?.budgetPlan?.budgetItems) {
       return;
     }
 
     this.allocation.budgetPlan.budgetItems.forEach((item: BudgetItem) => {
       if (!item.fileStorageLocation) {
         return;
       }
 
       this.fileService.listDocuments(item.fileStorageLocation).pipe(takeUntil(this.destroy$)).subscribe({
         next: (files) => {
           this.budgetItemFileMap[item.id.toString()] = files;
         },
         error: (err) => {
           console.error('Error loading budget item attachments for item:', item.id, err);
         },
       });
     });
   }
 
   downloadFile(file: FileInfo): void {
     this.fileService.downloadDocument(file.link).pipe(takeUntil(this.destroy$)).subscribe({
       next: (blob) => {
         const url = window.URL.createObjectURL(blob);
         const a = document.createElement('a');
         a.href = url;
         a.download = file.name;
         a.click();
         window.URL.revokeObjectURL(url);
       },
       error: (err) => {
         console.error('Error downloading document', err);
       }
     });
   }
 
   getFundingSourceTypeLabel(type: number): string {
     switch (type) {
       case 1:
         return 'I. Vlastiti izvori';
       case 2:
         return 'II. Ostala tijela javne vlasti';
       case 3:
         return 'III. Jedinice lokalne i područne (regionalne) samouprave';
       case 4:
         return 'IV. Drugo';
       default:
         return 'Nepoznat izvor';
     }
   }
}
